import {
  type UseFieldApiConfig,
  useFieldApi,
} from '@data-driven-forms/react-form-renderer';
import { forwardRef, useEffect } from 'react';

import FormControl from '~/common/componentMapper/FormControl';
import FormErrorMessage from '~/common/componentMapper/FormErrorMessage';
import FormLabel from '~/common/componentMapper/FormLabel';
import ChakraSelect from '~/common/components/Selects/Select';

import { validationError } from '../utils';
import SelectWithToolTip from './SelectWithToolTip';

type SelectProps = UseFieldApiConfig;

const Select = forwardRef<any, SelectProps>((props, ref) => {
  const {
    id,
    options = [],
    input,
    label,
    ariaLabel,
    onClick,
    placeholder,
    fcStyle,
    meta,
    validateOnMount,
    disabled,
    sx,
    role,
    showToolTip,
    onChange,
    onBlur,
    onFocus,
    isRequired,
    dataTestId,
  } = useFieldApi({
    ...props,
    type: 'select',
  });

  const { name } = props;

  const invalid = validationError(meta, validateOnMount);
  const text = invalid || ((meta.touched || validateOnMount) && meta.warning);

  useEffect(() => {
    if (input) {
      input.onChange(`${input.value}`);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  //Show label in the tooltip incase value is different from label
  const findLabelForTooltip = options?.find((m) => m.value === input.value);
  const cutOffLength = 10;

  return (
    <FormControl
      sx={{ ...fcStyle }}
      isInvalid={!!invalid}
      defaultValue={meta.initial}
      role={role ?? 'group'}
      isRequired={isRequired && label}
    >
      {label && (
        <FormLabel mb="unset" htmlFor={id ?? name}>
          {label}
        </FormLabel>
      )}
      {showToolTip && findLabelForTooltip?.label.length > cutOffLength ? (
        <SelectWithToolTip
          name={name}
          options={options}
          input={input}
          label={label}
          onClick={(e) => {
            input.onChange(e);
            onChange?.(e);
          }}
          placeholder={placeholder}
          isDisabled={disabled}
          sx={sx}
          invalid={invalid}
          ref={ref}
          dataTestId={dataTestId}
        />
      ) : (
        <ChakraSelect
          name={name}
          id={id ?? name}
          aria-label={ariaLabel}
          onClick={onClick}
          placeholder={placeholder}
          isInvalid={!!invalid}
          isDisabled={disabled}
          data-testid={dataTestId}
          {...input}
          onChange={(e) => {
            input.onChange(e);
            onChange?.(e);
          }}
          onBlur={(e) => {
            input.onBlur(e);
            onBlur?.(e);
          }}
          onFocus={(e) => {
            input.onFocus(e);
            onFocus?.(e);
          }}
          {...(input.value === '' && { color: 'gray' })}
          sx={{
            backgroundColor: 'white',
            whiteSpace: sx?.whiteSpace ? sx?.whiteSpace : 'break-spaces',
            minHeight: '2.5rem',
            height: 'auto',
            ...sx,
          }}
          ref={ref}
        >
          {!!options.length &&
            options.map(({ label, value }) => (
              <option
                key={`${name}-${value}`}
                value={value}
                data-testId={`${dataTestId}-option`}
              >
                {label}
              </option>
            ))}
        </ChakraSelect>
      )}
      {!!invalid && <FormErrorMessage>{text}</FormErrorMessage>}
    </FormControl>
  );
});

Select.displayName = 'SelectWithRef';

export default Select;
